import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Cell } from "recharts";
import { TrendingUp, FileText, Building2, Target, ArrowUpRight } from "lucide-react";

const STAGE_COLORS = {
  Lead: "#94A3B8", Contatto: "#0EA5E9", Preventivo: "#F59E0B", Vinto: "#10B981", Perso: "#EF4444",
};

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono-label text-[10px] text-slate-500">{label}</div>
        <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display font-black text-3xl text-slate-900 tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="p-10 font-mono-label text-sm text-slate-400">CARICAMENTO...</div>;

  const chartData = Object.entries(stats.by_stage).map(([stage, count]) => ({ stage, count }));

  return (
    <div className="p-10">
      <PageHeader subtitle="PANORAMICA" title="Dashboard" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Kpi label="Fatturato (Vinto)" value={eur(stats.revenue)} icon={TrendingUp} accent="#10B981" />
        <Kpi label="Valore Offerte Aperte" value={eur(stats.open_value)} icon={FileText} accent="#0052CC" />
        <Kpi label="Tasso Conversione" value={`${stats.conversion}%`} icon={Target} accent="#F59E0B" />
        <Kpi label="Aziende Clienti" value={stats.companies} icon={Building2} accent="#0EA5E9" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <div className="font-mono-label text-[10px] text-slate-500 mb-6">OFFERTE PER FASE PIPELINE</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={64}>
                {chartData.map((e) => <Cell key={e.stage} fill={STAGE_COLORS[e.stage]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col">
          <div className="font-mono-label text-[10px] text-slate-500 mb-6">RIEPILOGO</div>
          <div className="space-y-4 flex-1">
            {[
              { l: "Offerte totali", v: stats.total_offers },
              { l: "Offerte aperte", v: stats.open_offers },
              { l: "Modelli a catalogo", v: stats.products },
            ].map((r) => (
              <div key={r.l} className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">{r.l}</span>
                <span className="font-display font-bold text-lg text-slate-900">{r.v}</span>
              </div>
            ))}
          </div>
          <Link data-testid="dashboard-goto-pipeline" to="/pipeline"
            className="mt-6 flex items-center justify-between bg-[#0A192F] text-white rounded-sm px-4 py-3 text-sm font-medium hover:bg-[#0052CC] transition-colors duration-200">
            Vai alla Pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
